import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import {
  TrendingUp,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  MousePointer,
  Facebook,
  Instagram,
  Linkedin,
  BarChart3,
  Calendar,
  RotateCcw
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/analytics/overview`, {
        params: { days: parseInt(period) }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleResetCounters = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir réinitialiser tous les compteurs d'analytics ? Cette action est irréversible.")) {
      return;
    }
    
    setResetting(true);
    try {
      await axios.post(`${API}/api/analytics/reset`);
      toast.success("Compteurs réinitialisés avec succès");
      fetchAnalytics();
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  const getNetworkIcon = (network) => {
    switch (network) {
      case "facebook": return <Facebook className="w-5 h-5 text-blue-600" />;
      case "instagram": return <Instagram className="w-5 h-5 text-pink-600" />;
      case "linkedin": return <Linkedin className="w-5 h-5 text-blue-700" />;
      default: return <TrendingUp className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const overviewStats = [
    { label: "Impressions", value: analytics?.totals?.impressions || 0, icon: Eye, color: "text-blue-500" },
    { label: "Engagements", value: analytics?.totals?.engagements || 0, icon: Heart, color: "text-red-500" },
    { label: "Clics", value: analytics?.totals?.clicks || 0, icon: MousePointer, color: "text-green-500" },
    { label: "Partages", value: analytics?.totals?.shares || 0, icon: Share2, color: "text-purple-500" }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
        <p className="text-muted-foreground mt-1">
          Suivez les performances de vos publications
        </p>
      </div>

      {/* Period Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              <span className="font-medium">Période d'analyse</span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetCounters}
                disabled={resetting}
                className="text-muted-foreground hover:text-destructive"
                data-testid="reset-analytics-btn"
              >
                <RotateCcw className={`w-4 h-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
                Réinitialiser compteurs
              </Button>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[180px]" data-testid="period-selector">
                  <SelectValue placeholder="Période" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 derniers jours</SelectItem>
                  <SelectItem value="30">30 derniers jours</SelectItem>
                  <SelectItem value="90">90 derniers jours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {overviewStats.map((stat, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value.toLocaleString()}</p>
                </div>
                <stat.icon className={`w-8 h-8 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Network Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Performance par réseau
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {analytics?.networks?.map((network, index) => (
              <div key={index} className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 mb-4">
                  {getNetworkIcon(network.name)}
                  <span className="font-medium capitalize">{network.name}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Posts</span>
                    <span className="font-medium">{network.posts || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Impressions</span>
                    <span className="font-medium">{(network.impressions || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Engagements</span>
                    <span className="font-medium">{(network.engagements || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Taux d'engagement</span>
                    <span className="font-medium">{network.engagement_rate || "0%"}</span>
                  </div>
                </div>
              </div>
            )) || (
              <p className="col-span-3 text-center text-muted-foreground py-8">
                Aucune donnée disponible
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Publications récentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics?.recent_posts?.length > 0 ? (
            <div className="space-y-4">
              {analytics.recent_posts.map((post, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    {getNetworkIcon(post.network)}
                    <div>
                      <p className="font-medium line-clamp-1">{post.content?.substring(0, 60)}...</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(post.published_date || post.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Eye className="w-4 h-4 text-muted-foreground" />
                      <span>{post.impressions || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Heart className="w-4 h-4 text-muted-foreground" />
                      <span>{post.likes || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4 text-muted-foreground" />
                      <span>{post.comments || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Aucune publication récente
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
